// src/pages/Finance/Accounting/Invoices/Helper/HandleDocumentPayment.jsx
import {
  message,
  DatePicker,
  Button,
  Badge,
  Tag,
  Popover,
  Modal,
  InputNumber,
  Space,
  Typography,
  Divider,
  List,
  Dropdown,
} from "antd"
import { updateDoc, doc, getDoc, serverTimestamp, arrayUnion } from "firebase/firestore"
import { db } from "../../../../firebase/config"
import { useState } from "react"
import dayjs from "dayjs"
import { useAuthContext } from "../../../../hooks/useAuthContext"
import { CheckOutlined, DollarOutlined, DownOutlined } from "@ant-design/icons"

const { Text } = Typography

// ─── paymentStatus values ──────────────────────────────────────────────────
// 0 = unpaid (no proforma)
// 1 = unpaid (has proforma advance)
// 2 = fully paid
// 3 = partially paid

export default function HandleDocumentPayment({
  record,
  collectionName = "invoices",
  onPaymentUpdate,
}) {
  const { user } = useAuthContext()

  // ── Full payment modal state ──
  const [fullPayModalOpen, setFullPayModalOpen] = useState(false)
  const [fullPayDate, setFullPayDate] = useState(dayjs())
  const [fullPayLoading, setFullPayLoading] = useState(false)

  // ── Partial payment modal state ──
  const [partialModalOpen, setPartialModalOpen] = useState(false)
  const [partialAmount, setPartialAmount] = useState(null)
  const [partialDate, setPartialDate] = useState(dayjs())
  const [partialLoading, setPartialLoading] = useState(false)

  // ─── Derived values ────────────────────────────────────────────────────────
  const totalSum = record?.totalData?.totalSum || 0
  const paidAmount = record?.paidAmount || 0
  const remaining = Math.max(0, totalSum - paidAmount).toFixed(2)

  // ─── Mark fully paid ──────────────────────────────────────────────────────

  const handleMarkPaid = async () => {
    setFullPayLoading(true)
    try {
      const docUpdateFields =
        collectionName === "invoices"
          ? {
              paymentStatus: 2,
              paidAmount: totalSum,
              paidDateInvoiceTimestamp: serverTimestamp(),
              paidDateInvoice: fullPayDate.toDate(),
            }
          : {
              paymentStatus: 2,
              paidDateProformaTimestamp: serverTimestamp(),
              paidDateProforma: fullPayDate.toDate(),
            }

      await updateDoc(doc(db, collectionName, record.id), docUpdateFields)

      // Update related orders
      const orderUpdateFields =
        collectionName === "invoices"
          ? {
              invoicePaymentStatus: 2,
              paidDateInvoiceTimestamp: serverTimestamp(),
              paidDateInvoice: fullPayDate.toDate(),
            }
          : {
              proformaPaymentStatus: 2,
              paidDateProformaTimestamp: serverTimestamp(),
              paidDateProforma: fullPayDate.toDate(),
            }

      if (record.orderReferenceId?.length) {
        await Promise.all(
          record.orderReferenceId.map((id) => updateDoc(doc(db, "orders", id), orderUpdateFields)),
        )
      }

      // When PROFORMA is marked paid → sync paidAmount to linked invoices
      if (collectionName === "proformas" && record.invoiceIds?.length) {
        const proformaTotal = record.totalData?.totalSum || 0
        await Promise.all(
          record.invoiceIds.map(async (invoiceId) => {
            const invoiceRef = doc(db, "invoices", invoiceId)
            const invoiceSnap = await getDoc(invoiceRef)
            if (!invoiceSnap.exists()) return
            const invoice = invoiceSnap.data()
            const invoiceTotal = invoice.totalData?.totalSum || 0
            const currentPaid = invoice.paidAmount || 0
            const newPaidAmount = Math.min(currentPaid + proformaTotal, invoiceTotal)
            const newStatus = newPaidAmount >= invoiceTotal ? 2 : 3
            await updateDoc(invoiceRef, {
              paidAmount: newPaidAmount,
              paymentStatus: newStatus,
              payments: arrayUnion({
                amount: proformaTotal,
                date: fullPayDate.toDate(),
                note: `Proforma ${record.docPrefix}${String(record.docNumber).padStart(4, "0")} paid`,
                recordedBy: user?.uid || "",
                recordedAt: new Date(),
              }),
            })
          }),
        )
      }

      // Update client credit
      if (record.orderReferenceId?.length) {
        const clientRef = doc(db, "clients", record.clientId)
        const clientSnap = await getDoc(clientRef)
        if (clientSnap.exists()) {
          const c = clientSnap.data()
          const priceValue = parseFloat(record.price) || 0
          let orderCost = 0
          if (record.selectedProduct?.machine === "Tape") {
            orderCost = priceValue * (parseFloat(record.rollCount) || 0)
          } else {
            orderCost = priceValue * ((parseFloat(record.labelCount) || 0) / 1000)
          }
          await updateDoc(clientRef, {
            creditUsed: (parseFloat(c.creditUsed) || 0) - orderCost,
            creditLeft: (parseFloat(c.creditLeft) || 0) + orderCost,
          })
        }
      }

      message.success(`${collectionName === "invoices" ? "Invoice" : "Proforma"} marked as paid.`)
      setFullPayModalOpen(false)
      onPaymentUpdate()
    } catch (error) {
      message.error(`Error: ${error.message}`)
    } finally {
      setFullPayLoading(false)
    }
  }

  // ─── Add partial payment ──────────────────────────────────────────────────

  const handleAddPartialPayment = async () => {
    if (!partialAmount || partialAmount <= 0) {
      message.warning("Enter a valid amount.")
      return
    }
    if (partialAmount > parseFloat(remaining)) {
      message.warning(`Amount exceeds remaining balance of ${remaining} EUR.`)
      return
    }

    setPartialLoading(true)
    try {
      const newPaidAmount = parseFloat((paidAmount + partialAmount).toFixed(2))
      const newStatus = newPaidAmount >= totalSum ? 2 : 3

      await updateDoc(doc(db, "invoices", record.id), {
        paidAmount: newPaidAmount,
        paymentStatus: newStatus,
        payments: arrayUnion({
          amount: partialAmount,
          date: partialDate.toDate(),
          note: "partial",
          recordedBy: user?.uid || "",
          recordedAt: new Date(),
        }),
      })

      if (record.orderReferenceId?.length) {
        await Promise.all(
          record.orderReferenceId.map((id) =>
            updateDoc(doc(db, "orders", id), { invoicePaymentStatus: newStatus }),
          ),
        )
      }

      message.success(`Payment of ${partialAmount} EUR recorded.`)
      setPartialModalOpen(false)
      setPartialAmount(null)
      setPartialDate(dayjs())
      onPaymentUpdate()
    } catch (error) {
      message.error(`Error: ${error.message}`)
    } finally {
      setPartialLoading(false)
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  const status = record.paymentStatus
  const { paidDateInvoiceTimestamp, paidDateProformaTimestamp } = record

  // ── Status 2: fully paid ──────────────────────────────────────────────────
  if (status === 2) {
    const ts = collectionName === "invoices" ? paidDateInvoiceTimestamp : paidDateProformaTimestamp
    const paymentDate = ts
      ? dayjs.unix(ts.seconds).format("DD.MM.YYYY HH:mm")
      : "Date not available"

    return (
      <Popover
        content={
          <div style={{ maxWidth: 180 }}>
            <p style={{ margin: 0 }}>
              <strong>Paid on:</strong>
            </p>
            <p style={{ margin: 0 }}>{paymentDate}</p>
          </div>
        }
        title="Payment Details"
        trigger="click"
        placement="top">
        <Tag color="success" style={{ cursor: "pointer" }}>
          Paid
        </Tag>
      </Popover>
    )
  }

  // ── Status 0, 1, 3: unpaid / partial ─────────────────────────────────────
  const dropdownItems = [
    {
      key: "full",
      icon: <CheckOutlined />,
      label: status === 3 ? "Mark as Fully Paid" : "Mark as Paid",
    },
    ...(collectionName === "invoices"
      ? [
          {
            key: "partial",
            icon: <DollarOutlined />,
            label: status === 3 ? "Add Payment" : "Partial Payment",
          },
        ]
      : []),
  ]

  const handleDropdownClick = ({ key }) => {
    if (key === "full") setFullPayModalOpen(true)
    if (key === "partial") setPartialModalOpen(true)
  }

  const statusTag =
    status === 3 ? (
      <Tag color="orange" style={{ margin: 0 }}>
        Partial ({paidAmount.toFixed(2)} / {totalSum.toFixed(2)})
      </Tag>
    ) : status === 1 ? (
      <Tag color="blue" style={{ margin: 0 }}>
        Unpaid (proforma)
      </Tag>
    ) : (
      <Tag color="red" style={{ margin: 0 }}>
        Unpaid
      </Tag>
    )

  return (
    <>
      {/* ── Full payment modal ── */}
      <Modal
        title={status === 3 ? "Mark as Fully Paid" : "Mark as Paid"}
        open={fullPayModalOpen}
        onCancel={() => {
          setFullPayModalOpen(false)
          setFullPayDate(dayjs())
        }}
        onOk={handleMarkPaid}
        okText="Confirm Payment"
        confirmLoading={fullPayLoading}
        width={340}>
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Total amount
            </Text>
            <div>
              <Text strong>{totalSum.toFixed(2)} EUR</Text>
            </div>
          </div>
          <div>
            <Text strong>Payment date</Text>
            <DatePicker
              style={{ width: "100%", marginTop: 4 }}
              value={fullPayDate}
              onChange={(d) => setFullPayDate(d)}
            />
          </div>
        </Space>
      </Modal>

      {/* ── Partial payment modal (invoices only) ── */}
      {collectionName === "invoices" && (
        <Modal
          title="Record Partial Payment"
          open={partialModalOpen}
          onCancel={() => {
            setPartialModalOpen(false)
            setPartialAmount(null)
            setPartialDate(dayjs())
          }}
          onOk={handleAddPartialPayment}
          okText="Record Payment"
          confirmLoading={partialLoading}
          width={400}>
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Invoice total
                </Text>
                <div>
                  <Text strong>{totalSum.toFixed(2)} EUR</Text>
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Paid so far
                </Text>
                <div>
                  <Text strong style={{ color: "#52c41a" }}>
                    {paidAmount.toFixed(2)} EUR
                  </Text>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Remaining
                </Text>
                <div>
                  <Text strong style={{ color: "#f5222d" }}>
                    {remaining} EUR
                  </Text>
                </div>
              </div>
            </div>

            <Divider style={{ margin: "4px 0" }} />

            <div>
              <Text strong>Amount received (EUR)</Text>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                min={0.01}
                max={parseFloat(remaining)}
                step={0.01}
                precision={2}
                value={partialAmount}
                onChange={setPartialAmount}
                placeholder={`Max ${remaining} EUR`}
              />
            </div>

            <div>
              <Text strong>Payment date</Text>
              <DatePicker
                style={{ width: "100%", marginTop: 4 }}
                value={partialDate}
                onChange={(d) => setPartialDate(d)}
              />
            </div>

            {record.payments?.length > 0 && (
              <>
                <Divider style={{ margin: "4px 0" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Payment history
                </Text>
                <List
                  size="small"
                  dataSource={record.payments || record.payment || []}
                  renderItem={(p) => {
                    const d = p.date?.seconds ? new Date(p.date.seconds * 1000) : p.date
                    return (
                      <List.Item style={{ padding: "3px 0" }}>
                        <Text style={{ fontSize: 12 }}>
                          {dayjs(d).format("DD.MM.YYYY")}
                          {" — "}
                          <Text strong style={{ fontSize: 12 }}>
                            {Number(p.amount).toFixed(2)} EUR
                          </Text>
                          {p.note && p.note !== "partial" && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {" "}
                              ({p.note})
                            </Text>
                          )}
                        </Text>
                      </List.Item>
                    )
                  }}
                />
              </>
            )}
          </Space>
        </Modal>
      )}

      {/* ── Dropdown trigger ── */}
      <Dropdown
        menu={{ items: dropdownItems, onClick: handleDropdownClick }}
        trigger={["click"]}
        placement="bottomRight">
        <div style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
          {statusTag}
          <DownOutlined style={{ fontSize: 10, color: "#888" }} />
        </div>
      </Dropdown>
    </>
  )
}
